import { useCallback, useEffect, useState } from "react";
import type { AdminSeries, I18nText } from "@shared";
import * as api from "./adminApi";
import I18nFields from "./I18nFields";

interface Props {
  eventId: string;
  onChanged: () => void;
}

const emptyName: I18nText = { fr: "" };

export default function SeriesPanel({ eventId, onChanged }: Props) {
  const [series, setSeries] = useState<AdminSeries[]>([]);
  const [nameI18n, setNameI18n] = useState<I18nText>(emptyName);
  const [icon, setIcon] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setSeries(await api.listSeries(eventId));
    } catch {
      setError("Impossible de charger les séries.");
    }
  }, [eventId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function reset(): void {
    setEditingId(null);
    setNameI18n(emptyName);
    setIcon("");
    setSortOrder(0);
  }

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const input = { nameI18n, icon: icon === "" ? null : icon, sortOrder };

    try {
      if (editingId === null) await api.createSeries(eventId, input);
      else await api.updateSeries(editingId, input);
      reset();
      await refresh();
      onChanged();
    } catch {
      setError("Enregistrement impossible. Le nom français est obligatoire.");
    }
  }

  async function remove(item: AdminSeries): Promise<void> {
    const confirmed = window.confirm(
      `Supprimer la série « ${api.fr(item.nameI18n)} » ?\n\n` +
        "Cela supprime aussi tous ses objets de l'app et les codes rattachés. " +
        "Action irréversible.",
    );
    if (!confirmed) return;
    await api.deleteSeries(item.id);
    await refresh();
    onChanged();
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Séries</h2>

      {error !== null && <p className="form__error">{error}</p>}

      <ul className="list">
        {series.map((item) => (
          <li key={item.id} className="list__item">
            <div className="list__main list__main--static">
              <span className="list__name">
                {item.icon !== null && <span className="list__icon">{item.icon}</span>}
                {api.fr(item.nameI18n)}
              </span>
              <span className="list__meta">
                ordre {item.sortOrder}
                {item.nameI18n.en != null && ` · EN : ${item.nameI18n.en}`}
              </span>
            </div>

            <div className="list__actions">
              <button
                type="button"
                className="button button--small"
                onClick={() => {
                  setEditingId(item.id);
                  setNameI18n(item.nameI18n);
                  setIcon(item.icon ?? "");
                  setSortOrder(item.sortOrder);
                }}
              >
                Modifier
              </button>
              <button
                type="button"
                className="button button--small button--danger"
                onClick={() => void remove(item)}
              >
                Supprimer
              </button>
            </div>
          </li>
        ))}
        {series.length === 0 && <li className="list__empty">Aucune série dans cet événement.</li>}
      </ul>

      <form className="form" onSubmit={(e) => void submit(e)}>
        <h3 className="form__title">{editingId === null ? "Nouvelle série" : "Modifier la série"}</h3>

        <I18nFields id="series-name" label="Nom de la série" value={nameI18n} onChange={setNameI18n} />

        <div className="form__row">
          <div className="form__col">
            <label className="form__label" htmlFor="series-icon">
              Icône (emoji, optionnel)
            </label>
            <input
              id="series-icon"
              className="form__input"
              type="text"
              value={icon}
              maxLength={8}
              placeholder="🪐"
              onChange={(e) => {
                setIcon(e.target.value);
              }}
            />
          </div>
          <div className="form__col">
            <label className="form__label" htmlFor="series-order">
              Ordre d'affichage
            </label>
            <input
              id="series-order"
              className="form__input"
              type="number"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(Number(e.target.value));
              }}
            />
          </div>
        </div>

        <div className="button-row">
          <button
            type="submit"
            className="button button--primary"
            disabled={nameI18n.fr.trim() === ""}
          >
            {editingId === null ? "Créer" : "Enregistrer"}
          </button>
          {editingId !== null && (
            <button type="button" className="button button--ghost" onClick={reset}>
              Annuler
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
